import React, { useState, useEffect, useRef } from 'react';
import {
    tradingApi,
    type CurrentUserContext,
    type ManagedAccountSummary,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import UserTypeBadge from './UserTypeBadge';

interface AccountSwitcherProps {
    onContextChange?: () => void;
    direction?: 'up' | 'down';
    align?: 'left' | 'right';
    variant?: 'default' | 'compact';
    showIdentity?: boolean;
    alwaysRender?: boolean;
}

const AccountSwitcher: React.FC<AccountSwitcherProps> = ({
    onContextChange,
    direction = 'down',
    align = 'right',
    variant = 'default',
    showIdentity = false,
    alwaysRender = false
}) => {
    const { login } = useAuth();
    const [context, setContext] = useState<CurrentUserContext | null>(null);
    const [managedAccounts, setManagedAccounts] = useState<ManagedAccountSummary[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchContext();
        fetchManagedAccounts();
    }, []);

    useEffect(() => {
        const id = window.setInterval(fetchManagedAccounts, 10000);
        return () => window.clearInterval(id);
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchContext = async () => {
        try {
            const response = await tradingApi.getCurrentContext();
            if (response.success && response.data) {
                setContext(response.data);
            }
        } catch (err) {
            console.warn('Failed to fetch context:', err);
        }
    };

    const fetchManagedAccounts = async () => {
        try {
            const response = await tradingApi.getManagedAccounts();
            if (response.success && response.data?.managed_accounts) {
                setManagedAccounts(response.data?.managed_accounts);
            }
        } catch (err) {
            console.warn('Failed to fetch managed accounts:', err);
        }
    };

    const handleSwitch = async (accountId: number) => {
        setLoading(true);
        try {
            const response = await tradingApi.switchToManagedAccount(accountId);
            if (response.access_token) {
                login(response.access_token);
                setIsOpen(false);
                await fetchContext();
                if (onContextChange) onContextChange();
                // Reload the page to refresh all data
                window.location.reload();
            }
        } catch (err) {
            console.warn('Failed to switch account:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchBack = async () => {
        setLoading(true);
        try {
            const response = await tradingApi.switchBackToOwnAccount();
            if (response.access_token) {
                login(response.access_token);
                setIsOpen(false);
                await fetchContext();
                if (onContextChange) onContextChange();
                window.location.reload();
            }
        } catch (err) {
            console.warn('Failed to switch back:', err);
        } finally {
            setLoading(false);
        }
    };

    const canSwitchContext = managedAccounts.length > 0 || !!context?.is_acting_as;

    // Don't render if no managed accounts and not acting as someone
    if (!alwaysRender && !canSwitchContext) {
        return null;
    }

    const displayName = context?.first_name
        ? `${context.first_name}${context.last_name ? ' ' + context.last_name.charAt(0) + '.' : ''}`
        : context?.username || 'User';
    const isIdentityCompact = variant === 'compact' && showIdentity;
    const dropdownAlignment = isIdentityCompact
        ? 'left-0 max-w-[calc(100vw-7.25rem)]'
        : `max-w-[calc(100vw-2rem)] ${align === 'right' ? 'right-0' : 'left-0'}`;

    return (
        <div
            className={`relative ${isIdentityCompact ? 'min-w-0 flex-1 max-w-[190px]' : ''}`}
            ref={dropdownRef}
        >
            <button
                onClick={() => {
                    if (canSwitchContext) setIsOpen(!isOpen);
                }}
                className={`flex items-center gap-2 rounded-lg transition-colors min-w-0 ${variant === 'compact'
                    ? (context?.is_acting_as
                        ? `${isIdentityCompact ? 'h-8 w-full px-2 justify-start' : 'w-7 h-7 justify-center flex-shrink-0'} bg-amber-900/30 border border-amber-600/50 text-amber-300 hover:bg-amber-900/50`
                        : `${isIdentityCompact ? 'h-8 w-full px-2 justify-start' : 'w-7 h-7 justify-center flex-shrink-0'} bg-gray-800 border border-gray-600 text-gray-300 ${canSwitchContext ? 'hover:bg-gray-700' : ''}`)
                    : `px-3 py-1.5 border w-full text-left ${context?.is_acting_as
                        ? 'bg-amber-900/30 border-amber-600/50 text-amber-300 hover:bg-amber-900/50'
                        : 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700'}`
                    }`}
                disabled={loading}
                title={variant === 'compact' ? displayName : undefined}
            >
                {/* User Icon */}
                <svg className={`flex-shrink-0 ${variant === 'compact' ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${context?.is_acting_as ? 'text-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.4)]' : 'text-neon shadow-[0_0_8px_rgba(var(--neon-rgb),0.3)]'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>

                {isIdentityCompact && (
                    <div className="min-w-0 flex flex-col items-start justify-center leading-none">
                        <span className="max-w-full truncate text-[11px] font-bold text-white">
                            {displayName}
                        </span>
                        <UserTypeBadge
                            role={context?.role}
                            compact
                            className="max-[360px]:hidden"
                        />
                    </div>
                )}

                {variant !== 'compact' && (
                    <>
                        <span className="text-sm font-medium truncate flex-1 min-w-0">{displayName}</span>

                        {context?.is_acting_as && (
                            <span className="text-[10px] px-1 py-0.5 bg-amber-600/30 rounded text-amber-200 whitespace-nowrap flex-shrink-0">
                                Acting As
                            </span>
                        )}

                        {/* Dropdown Arrow */}
                        <svg className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </>
                )}

                {isIdentityCompact && canSwitchContext && (
                    <svg className={`w-3 h-3 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                )}
            </button>

            {isOpen && (
                <div className={`absolute w-64 ${dropdownAlignment} bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 ${
                    direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
                    }`}>
                    <div className="p-3 border-b border-gray-700">
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Current Account</p>
                        <div className="mt-1 flex items-center justify-between gap-2">
                            <p className="min-w-0 truncate text-sm font-medium text-gray-200" title={context?.username}>{context?.username}</p>
                            {context && <UserTypeBadge role={context.role} compact />}
                        </div>
                        {context?.is_acting_as && context.original_user && (
                            <p className="text-xs text-amber-400 mt-1 truncate">
                                Managed by: {context.original_user.username}
                            </p>
                        )}
                    </div>

                    {context?.is_acting_as && (
                        <button
                            onClick={handleSwitchBack}
                            disabled={loading}
                            className="w-full px-3 py-2 text-left text-sm text-blue-400 hover:bg-gray-700 transition-colors flex items-center gap-2"
                        >
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
                            </svg>
                            Switch Back to My Account
                        </button>
                    )}

                    {managedAccounts.length > 0 && (
                        <>
                            <div className="px-3 py-2 border-t border-gray-700">
                                <p className="text-xs text-gray-500 uppercase tracking-wider">Managed Accounts</p>
                            </div>
                            {managedAccounts.map((account) => (
                                <button
                                    key={account.id}
                                    onClick={() => handleSwitch(account.id)}
                                    disabled={loading || context?.user_id === account.id}
                                    className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center gap-2 ${context?.user_id === account.id
                                        ? 'text-gray-500 cursor-not-allowed'
                                        : 'text-gray-300 hover:bg-gray-700'
                                        }`}
                                >
                                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                    </svg>
                                    <span className="min-w-0 flex-1 truncate">
                                        {account.first_name || account.username}
                                        {account.last_name && ` ${account.last_name}`}
                                    </span>
                                    <UserTypeBadge role={account.role} compact />
                                    {context?.user_id === account.id && (
                                        <span className="ml-auto text-xs text-gray-600 flex-shrink-0">(current)</span>
                                    )}
                                </button>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default AccountSwitcher;
